import { useCallback, useMemo, useState, useSyncExternalStore } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { SuggesterSlot, SuggesterStation, SuggesterProps } from '@metro/suggester'
import { buildSuggesterStrings } from '../i18n/packageStrings'
import { useMetroMapDispatch, useMetroMapStore } from './useMetroMapStore'
import { effectiveTheme } from './effectiveTheme'
import { buildSuggesterStations, buildSuggesterLines } from './suggesterAdapter'
import {
  favoritesService,
  buildSearchService,
  buildPopular,
  resolveNearby,
  probeLocationPermission,
} from './suggesterServices'
import { stationSnapshotFromSchema } from './stationSnapshotFromSchema'
import { resolveStationById } from '../interaction/stationResolver'
import { reportAnalyticsEvent, realMetroAnalyticsReporter } from '../analytics/webAnalyticsReporter'
import { reportSearchEntryTapped } from '../analytics/mapSurfaceEvents'
import { useSchema } from '../components/useSchema'
import { useStationStatuses } from './stationStatusContext'
import { toSuggesterCachedStatuses } from './suggesterStatusAdapter'

/**
 * Query param that opens Suggester. `?suggester=from` or
 * `?suggester=to` — the value names the route slot being filled
 * (TASK-318 spec §6). Anything else is treated as "closed".
 */
export const PARAM_SUGGESTER = 'suggester'

function parseSlot(value: string | null): SuggesterSlot | null {
  return value === 'from' || value === 'to' ? value : null
}

/**
 * Shared Suggester host wiring (TASK-412) — the service builders, the
 * `?suggester=` slot param, and the selection→slot→dismiss handler that
 * were previously inlined in {@link MobileRouteCardOverlay}.
 *
 * Both the phone overlay (`MobileRouteCardOverlay`,
 * `layout="overlay"`) and the tablet/desktop side panel
 * (`StationCardOrDetailsRouter`, `layout="sidePanel"`) need the identical
 * picker wiring; hoisting it here keeps a single source of truth so the
 * two surfaces can never drift (spec §5 — the output contract is reused,
 * not re-implemented per surface). The hook is form-factor-agnostic; the
 * host picks the `layout` when it spreads {@link SuggesterPanelBindings.props}.
 */
export interface SuggesterPanelBindings {
  /** The open slot, or `null` when the picker is closed. */
  readonly slot: SuggesterSlot | null
  /**
   * Props ready to spread onto `<Suggester {...bindings.props(slot)} />`.
   * Excludes `layout` (the host supplies it) and `slot` (passed
   * explicitly so the caller controls the open slot). Includes the
   * controlled `query` (TASK-412 pills-as-search) — ignored by the
   * phone overlay (it owns its own SearchField), consumed by the
   * sidePanel (where the active pill is the query input).
   */
  readonly props: (slot: SuggesterSlot) => Omit<SuggesterProps, 'layout'>
  /** Open the picker for `slot` by writing `?suggester=`. */
  readonly open: (slot: SuggesterSlot) => void
  /** Close the picker by dropping `?suggester=`. */
  readonly close: () => void
  /**
   * The transient pills-as-search query (TASK-412) — the text typed into
   * the active RouteCard pill in the side panel. Empty when no slot is
   * open or the user hasn't typed. Reset to `''` whenever the active slot
   * switches (so each slot starts at its idle sections).
   */
  readonly query: string
  /** Write the active pill's query (the host pill input's onChange). */
  readonly setQuery: (query: string) => void
}

export function useSuggesterPanel(): SuggesterPanelBindings {
  const schema = useSchema()
  const dispatch = useMetroMapDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation('suggester')

  // Locale-aware copy for the domain-agnostic `@metro/suggester`
  // package (host-side i18n injection). en values are byte-identical to
  // the package's `defaultStrings`, so the en render is unchanged.
  const strings = useMemo(() => buildSuggesterStrings(t), [t])

  // Catalogue + derived services, memoised on the schema reference
  // (stable once the SchemaProvider promise resolves). Identical to the
  // builders the overlay used inline.
  // The EFFECTIVE theme — the signal that picks the map's MMTG
  // geometry, night-tube included; suggester row dots / disruption
  // chips must match the map (2026-07 contract).
  const store = useMetroMapStore()
  const theme = useSyncExternalStore(store.subscribe, () => effectiveTheme(store.getState()))
  const catalogue = useMemo(() => buildSuggesterStations(schema, theme), [schema, theme])
  const searchService = useMemo(() => buildSearchService(catalogue), [catalogue])
  const popular = useMemo(() => buildPopular(catalogue), [catalogue])
  const lines = useMemo(() => buildSuggesterLines(schema, theme), [schema, theme])
  const nearby = useMemo(() => resolveNearby(schema, catalogue), [schema, catalogue])

  // TASK-629 — the live disruption snapshot (StationStatusController →
  // StationStatusContext), adapted onto the Suggester's flat
  // `cachedStatuses` shape (spec §8/§13) so idle + results rows across
  // EVERY section (Nearby / Favorites / Recents / Popular) flip to their
  // Closed / Advisory badge. Previously never read here at all — the web
  // mirror of the Android gap where the snapshot reached the station card
  // but was never wired into the suggester (see suggesterStatusAdapter.ts).
  const stationStatuses = useStationStatuses()
  const cachedStatuses = useMemo(
    () => toSuggesterCachedStatuses(stationStatuses),
    [stationStatuses],
  )

  const slot = parseSlot(searchParams.get(PARAM_SUGGESTER))

  // Transient pills-as-search query (TASK-412), owned here so the active
  // RouteCard pill (the side-panel query input) and the suggester read
  // one source of truth. The query must reset whenever the active slot
  // changes — a fresh slot starts at its idle sections (decision #2:
  // switching clears the transient query, including a return to a slot
  // previously typed in).
  //
  // Implemented with React's documented "store info from previous renders"
  // pattern (https://react.dev/reference/react/useState#storing-information-from-previous-renders):
  // the last-seen slot lives in STATE (not a ref, not an effect), and the
  // reset adjusts state DURING RENDER. Calling `setState` during render of
  // the same component is supported — React restarts the render with the
  // updated state and discards the in-progress output before committing,
  // so it is neither the `set-state-in-effect` cascade nor a render-time
  // ref write (both lint-flagged), and the consumer never observes the
  // pre-reset value.
  const [query, setQuery] = useState('')
  const [lastSlot, setLastSlot] = useState<SuggesterSlot | null>(slot)
  if (lastSlot !== slot) {
    setLastSlot(slot)
    setQuery('')
  }

  const open = useCallback(
    (next: SuggesterSlot) => {
      // G-1 `search_entry_tapped { slot }` — the user tapped the search /
      // route entry on the map to open the picker. Fires BEFORE the
      // Suggester mount (which then fires `suggester_open`), giving F2 its
      // map-surface entry attribution (search-vs-station-tap path). `slot`
      // = the route slot being filled (`from` | `to`).
      reportSearchEntryTapped(realMetroAnalyticsReporter, { slot: next })
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.set(PARAM_SUGGESTER, next)
          return params
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.delete(PARAM_SUGGESTER)
        return params
      },
      { replace: true },
    )
  }, [setSearchParams])

  // Write the picked station into the route slot, then dismiss the
  // Suggester in ONE atomic param update (drop `?suggester=` AND set the
  // slot slug) so the two `useSearchParams` writers don't race on stale
  // `prev`. Identical to the overlay's `handleStationSelected`.
  const onStationSelected = useCallback(
    (station: SuggesterStation, target: SuggesterSlot) => {
      const resolved = resolveStationById(schema, station.id)
      if (resolved !== null) {
        dispatch({
          kind: target === 'from' ? 'RouteFromSelected' : 'RouteToSelected',
          station: stationSnapshotFromSchema(resolved),
        })
      }
      const slug = resolved?.station.slug
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          params.delete(PARAM_SUGGESTER)
          if (slug !== undefined) params.set(target, slug)
          return params
        },
        { replace: true },
      )
    },
    [schema, dispatch, setSearchParams],
  )

  const props = useCallback(
    (target: SuggesterSlot): Omit<SuggesterProps, 'layout'> => ({
      slot: target,
      catalogue,
      popular,
      searchService,
      favoritesService,
      onStationSelected,
      onClose: close,
      onAnalytics: reportAnalyticsEvent,
      resolveNearby: nearby,
      probePermission: probeLocationPermission,
      lines,
      strings,
      // Controlled query for the sidePanel pills-as-search (TASK-412).
      // The phone overlay ignores it (owns its own SearchField).
      query,
      // TASK-629 — live disruption badges, previously never forwarded.
      cachedStatuses,
    }),
    [
      catalogue,
      popular,
      searchService,
      onStationSelected,
      close,
      nearby,
      lines,
      strings,
      query,
      cachedStatuses,
    ],
  )

  return { slot, props, open, close, query, setQuery }
}
